import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../constants";

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map(c => c.charCodeAt(0)));
};

export const usePushNotifications = (token) => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(isSupported);
    if (isSupported) {
      setPermission(Notification.permission);
      // Check if already subscribed
      navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription())
        .then(sub => setSubscribed(!!sub))
        .catch(() => {});
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported || !token) return;
    setLoading(true);
    try {
      // Get VAPID public key
      const { data } = await axios.get(`${API}/push/vapid-key`);
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });

      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubscribed(true);
      setPermission('granted');
    } catch (err) {
      if (err?.name === 'NotAllowedError') setPermission('denied');
      console.warn('Push subscribe error:', err);
    }
    setLoading(false);
  }, [supported, token]);

  const unsubscribe = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
      await axios.delete(`${API}/push/unsubscribe`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscribed(false);
    } catch (err) {
      console.warn('Push unsubscribe error:', err);
    }
    setLoading(false);
  }, [token]);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe };
};
